import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import clsx from "clsx";
import DialogContentText from "@material-ui/core/DialogContentText";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import Typography from "@material-ui/core/Typography";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import { Formik, Form } from "formik";
import { Input, Select, Checkbox } from "../../components/Formik";
import * as Yup from "yup";
import { Actions } from "../../components/Dialogs";
import { LABEL_OPTIONS } from "./constants";

const useStyles = makeStyles((theme) => ({
  row: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  space: {
    justifyContent: "space-between",
  },
}));

export default function Labels({ push, remove, form }) {
  const classes = useStyles();

  const picker = React.useRef();

  const onAdd = async () => {
    try {
      const values = await picker.current.open({
        name: "",
        width: 80,
        align: "left",
      });
      push({ ...values });
    } catch (err) {}
  };

  const onEdit = async (e, i) => {
    try {
      const values = await picker.current.edit({ ...e });
      form.setFieldValue(`labels[${i}]`, values);
    } catch (err) {}
  };

  const getName = (item) => {
    if (item?.isCustom) return item?.customText;
    const found = LABEL_OPTIONS.find((t) => t.value === item.name);
    return found?.label ?? "";
  };

  return (
    <div>
      <div className={clsx(classes.row, classes.space)}>
        <DialogContentText>Labels</DialogContentText>
      </div>

      {form?.values?.labels?.map((item, index) => (
        <div key={index} className={clsx(classes.row, classes.space)}>
          <Typography>{getName(item)}</Typography>

          <div className={classes.row}>
            <IconButton
              color="inherit"
              size="small"
              onClick={() => onEdit(item, index)}
            >
              <EditIcon fontSize="small" />
            </IconButton>

            <IconButton
              color="inherit"
              size="small"
              onClick={() => remove(index)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </div>
        </div>
      ))}

      <Button
        color="primary"
        onClick={onAdd}
        startIcon={<AddIcon />}
        size="small"
      >
        Add Item
      </Button>

      <Picker ref={picker} />
    </div>
  );
}

const Picker = React.forwardRef((props, ref) => {
  const classes = useStyles();

  const [open, setOpen] = React.useState(false);
  const [newItem, setNewItem] = React.useState(null);
  const [initialValues, setInitialValues] = React.useState(null);

  const promiseRef = React.useRef();

  React.useImperativeHandle(ref, () => ({
    open(e) {
      setNewItem(true);
      setInitialValues(e);
      setOpen(true);
      return new Promise((resolve, reject) => {
        promiseRef.current = { resolve, reject };
      });
    },
    edit(e) {
      setNewItem(false);
      setInitialValues(e);
      setOpen(true);
      return new Promise((resolve, reject) => {
        promiseRef.current = { resolve, reject };
      });
    },
  }));

  const onClose = () => {
    setOpen(false);
    promiseRef.current.reject();
  };

  const onSubmit = (values) => {
    setOpen(false);
    promiseRef.current.resolve(values);
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        enableReinitialize={true}
        validateOnChange={true}
        validationSchema={Yup.object({
          isCustom: Yup.bool(),
          isMultiplied: Yup.bool(),
          customText: Yup.string().when("isCustom", {
            is: true,
            then: Yup.string().required("Required"),
            otherwise: Yup.string().nullable(),
          }),
          multiplier: Yup.number().when("isMultiplied", {
            is: true,
            then: Yup.number().required("Required"),
            otherwise: Yup.number().nullable(),
          }),
          name: Yup.string().when("isCustom", {
            is: false,
            then: Yup.string().required("Required"),
            otherwise: Yup.string().nullable(),
          }),
          top: Yup.number().min(0).required("Required"),
          left: Yup.number().min(0).required("Required"),
          width: Yup.number().min(0).required("Required"),
          align: Yup.string().required("Required"),
        })}
      >
        {({ dirty, isValid, values }) => (
          <Form>
            <DialogTitle>{`${newItem ? "New" : "Edit"} Item`}</DialogTitle>

            <DialogContent className={classes.formContent}>
              <div className={classes.row}>
                <Checkbox name="isCustom" label="Custom Text" />
                <Checkbox name="isMultiplied" label="Multiply" />
              </div>
              {values?.isMultiplied ? (
                <div className={classes.row}>
                  <Select
                    name="name"
                    label="Option"
                    options={LABEL_OPTIONS}
                    right={16}
                    width={400}
                  />
                  <Input name="multiplier" label="Multiplier" type="number" />
                </div>
              ) : (
                <>
                  {values?.isCustom ? (
                    <Input name="customText" label="Custom Text" />
                  ) : (
                    <Select
                      name="name"
                      label="Option"
                      options={LABEL_OPTIONS}
                    />
                  )}
                </>
              )}
              <Input name="top" label="Top" type="number" />
              <Input name="left" label="Left" type="number" />
              <Input name="width" label="Width" type="number" />
              <Select
                name="align"
                label="Text align"
                options={[
                  { label: "Left", value: "left" },
                  { label: "Center", value: "center" },
                  { label: "Right", value: "right" },
                ]}
              />
            </DialogContent>

            <Actions
              newItem={newItem}
              onClose={onClose}
              dirty={dirty}
              isValid={isValid}
            />
          </Form>
        )}
      </Formik>
    </Dialog>
  );
});
